import { Link, useNavigate, useParams } from 'react-router-dom'
import { Banner } from '@/components/Banner'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { Page, PageHeader } from '@/components/Page'
import { Spinner } from '@/components/Spinner'
import { formatVisitDate } from '@/lib/dates'
import { ConvertCallerFields, useConvertCaller } from './ConvertCallerForm'
import { errorMessage } from '@/api/messages'

/**
 * Converting an enquiry as a whole page. In the app this opens as a dialog from
 * the enquiry itself; the route stays openable directly, and both shells share
 * one form.
 */
export function ConvertCallerScreen() {
  const { callerId = '' } = useParams()
  const navigate = useNavigate()

  const state = useConvertCaller({
    callerId,
    onConverted: (result) => navigate(`/patients/${result.patient.id}`, { replace: true }),
  })
  const { caller } = state

  if (caller.isLoading) return <Spinner label="Loading enquiry" />
  if (caller.error) return <Banner tone="danger">{errorMessage(caller.error)}</Banner>
  if (caller.data?.status === 'became_patient') {
    return (
      <Page>
        <Banner tone="info">This enquiry has already become a patient.</Banner>
        <p className="mt-4">
          <Link to={`/patients/${caller.data.converted_patient_id}`}>Open the patient record</Link>
        </p>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Booking"
        title="Convert to patient"
        {...(caller.data
          ? {
              description: `From the call on ${formatVisitDate(caller.data.call_date)}. Nothing already taken down needs retyping.`,
            }
          : {})}
      />

      {state.error && <Banner tone="danger">{state.error}</Banner>}

      <form className="grid gap-4" onSubmit={state.submit}>
        <ConvertCallerFields state={state} />

        <div className="flex flex-wrap items-center justify-end gap-3">
          <Link to={`/callers/${callerId}`}>Back to the enquiry</Link>
          <span className="flex-1" />
          <Button type="submit" loading={state.convert.isPending}>
            <Icon name="userPlus" />
            Convert to patient
          </Button>
        </div>
      </form>
    </Page>
  )
}
